'use client';

import React from 'react';

import Link from 'next/link';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useRequestResetPassword } from '@kit/supabase/hooks/use-request-reset-password';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { resolveResetRequestErrorMessage } from '../../lib/auth-error-message';
import { NativeAuthButton } from './native-auth-button';
import { NativeAuthShell } from './native-auth-shell';
import { NativeGroupedInput, NativeInputRow } from './native-grouped-input';

const PasswordResetRequestSchema = z.object({
  email: z.string().email(),
});

export function NativePasswordResetPage({
  logoUrl,
  accountName,
  primaryColor,
  secondaryColor,
  appBackgroundColor = '#FFFFFF',
  appTextColor = '#1C1C1C',
  signInPath,
  redirectPath,
}: {
  logoUrl?: string | null;
  accountName?: string;
  primaryColor: string;
  secondaryColor: string;
  appBackgroundColor?: string;
  appTextColor?: string;
  signInPath: string;
  redirectPath: string;
}) {
  const { t } = useTranslation();
  const resetPassword = useRequestResetPassword();
  const [emailSent, setEmailSent] = React.useState(false);

  const isDark = isDarkColor(appBackgroundColor);
  const inputClass = isDark
    ? 'w-full bg-transparent text-base text-white outline-none placeholder:text-white/40'
    : 'w-full bg-transparent text-base text-gray-900 outline-none placeholder:text-gray-400';

  const form = useForm({
    resolver: zodResolver(PasswordResetRequestSchema),
    defaultValues: { email: '' },
  });

  const handleSubmit = async (data: z.infer<typeof PasswordResetRequestSchema>) => {
    try {
      const redirectTo = new URL(redirectPath, window.location.origin).href;

      await resetPassword.mutateAsync({
        email: data.email,
        redirectTo,
      });

      setEmailSent(true);
    } catch (error) {
      // Enumeration-safe: only operational errors (rate limits) are surfaced;
      // anything that could reveal whether the email exists stays generic.
      toast.error(resolveResetRequestErrorMessage(error, t));
    }
  };

  // Success state
  if (emailSent) {
    return (
      <NativeAuthShell
        logoUrl={logoUrl}
        accountName={accountName}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        appBackgroundColor={appBackgroundColor}
        appTextColor={appTextColor}
        showBackButton
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-center gap-3 text-center">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full"
              style={{ backgroundColor: `${appTextColor}20` }}
            >
              <svg
                className="h-8 w-8"
                style={{ color: appTextColor }}
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
            </div>
            <h2
              className="text-xl font-semibold"
              style={{ color: appTextColor }}
            >
              <Trans i18nKey="auth:passwordResetEmailSent" defaults="Check your email" />
            </h2>
            <p
              className="text-sm"
              style={{ color: `${appTextColor}80` }}
            >
              <Trans
                i18nKey="auth:passwordResetSuccessMessage"
                defaults="We sent a password reset link to your email address. Click the link to set a new password."
              />
            </p>
          </div>

          <Link
            href={signInPath}
            className="flex min-h-[44px] items-center justify-center text-sm font-medium"
            style={{ color: appTextColor }}
          >
            <Trans i18nKey="auth:backToSignIn" defaults="Back to Sign In" />
          </Link>
        </div>
      </NativeAuthShell>
    );
  }

  return (
    <NativeAuthShell
      logoUrl={logoUrl}
      accountName={accountName}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
      appBackgroundColor={appBackgroundColor}
      appTextColor={appTextColor}
      showBackButton
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h2
            className="text-xl font-semibold"
            style={{ color: appTextColor }}
          >
            <Trans i18nKey="auth:passwordResetLabel" defaults="Reset Password" />
          </h2>
          <p
            className="text-sm"
            style={{ color: `${appTextColor}80` }}
          >
            <Trans
              i18nKey="auth:passwordResetSubheading"
              defaults="Enter your email and we'll send you a link to reset your password."
            />
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-5"
          >
            <NativeGroupedInput appBackgroundColor={appBackgroundColor}>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <NativeInputRow appTextColor={appTextColor}>
                        <input
                          type="email"
                          autoComplete="email"
                          placeholder="Email"
                          disabled={resetPassword.isPending}
                          className={inputClass}
                          data-test="native-reset-email-input"
                          {...field}
                        />
                      </NativeInputRow>
                    </FormControl>
                    <FormMessage className="px-1 text-red-400" />
                  </FormItem>
                )}
              />
            </NativeGroupedInput>

            <NativeAuthButton
              type="submit"
              disabled={resetPassword.isPending}
              loading={resetPassword.isPending}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              appTextColor={appTextColor}
            >
              <Trans i18nKey="auth:passwordResetLabel" defaults="Reset Password" />
            </NativeAuthButton>
          </form>
        </Form>

        <div className="flex justify-center">
          <Link
            href={signInPath}
            className="flex min-h-[44px] items-center text-sm font-medium"
            style={{ color: appTextColor }}
          >
            <Trans i18nKey="auth:backToSignIn" defaults="Back to Sign In" />
          </Link>
        </div>
      </div>
    </NativeAuthShell>
  );
}

function isDarkColor(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}
